import { useMemo } from "react";
import { Alert, Button, Card, Col, Form, Row } from "react-bootstrap";
import { useGetPackages } from "../../../../../api/fleet-management/packages/packages.hooks";
import { MdDelete } from "react-icons/md";

const BookingVehicleRow = ({
  vehicle,
  index,
  vehicles,
  vehicleRowsCount,
  selectedVehicleIds,
  fieldErrors,
  isCreatingBooking,
  onVehicleChange,
  onRemoveVehicle,
}) => {
  const selectedVehicle = useMemo(
    () => vehicles.find((item) => item?._id === vehicle.vehicleId),
    [vehicle.vehicleId, vehicles]
  );

  const { data: packagesData, isLoading: isLoadingPackages } = useGetPackages(
    selectedVehicle?._id ? { vehicleId: selectedVehicle._id } : undefined
  );

  const packages = useMemo(() => packagesData, [packagesData]);

  return (
    <div className="mb-3 p-3 rounded border">
      <Row className="g-2 align-items-end">
        <Col md={11}>
          <Form.Group className="mb-3">
            <Form.Label className="form-label form-label-sm">
              Vehicle {index + 1} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={vehicle.vehicleId}
              onChange={(e) => onVehicleChange(index, "vehicleId", e.target.value)}
              disabled={isCreatingBooking}
              isInvalid={!!fieldErrors[`vehicle_${index}_id`]}
            >
              <option value="">Select vehicle</option>
              {vehicles
                .filter((item) => {
                  if (!item?._id) return false;
                  if (item._id === vehicle.vehicleId) return true;
                  return !selectedVehicleIds.includes(item._id);
                })
                .map((item) => (
                  <option key={item?._id} value={item?._id}>
                    {`${item?.name || "Vehicle"} | ${item?.fuelType || "-"} | Seats: ${
                      item?.specifications?.seatingCapacity ?? "-"
                    }`}
                  </option>
                ))}
            </Form.Select>
            {fieldErrors[`vehicle_${index}_id`] && (
              <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
                {fieldErrors[`vehicle_${index}_id`]}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group>
            <Form.Label className="form-label form-label-sm">
              Package <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={vehicle.packageId}
              onChange={(e) => onVehicleChange(index, "packageId", e.target.value)}
              disabled={isCreatingBooking || !vehicle.vehicleId || isLoadingPackages}
              isInvalid={!!fieldErrors[`vehicle_${index}_packageId`]}
            >
              <option value="">
                {!vehicle.vehicleId
                  ? "Select a vehicle first"
                  : isLoadingPackages
                    ? "Loading packages..."
                    : "Select package"}
              </option>
              {packages?.map((item) => (
                <option key={item?._id} value={item?._id}>
                  {item?.description || "Package"}
                </option>
              ))}
            </Form.Select>
            {fieldErrors[`vehicle_${index}_packageId`] && (
              <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
                {fieldErrors[`vehicle_${index}_packageId`]}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>

        <Col md={1}>
          <Button
            type="button"
            variant="outline-light"
            onClick={() => onRemoveVehicle(index)}
            disabled={isCreatingBooking || vehicleRowsCount === 1}
            className="w-100 border text-danger fw-bold"
            aria-label={`Remove vehicle ${index + 1}`}
          >
            <MdDelete size={20} />
          </Button>
        </Col>
      </Row>
    </div>
  );
};

const BookingVehiclesSection = ({
  form,
  fieldErrors,
  vehicles,
  selectedVehicleIds,
  isCreatingBooking,
  onVehicleChange,
  onRemoveVehicle,
  onAddVehicle,
}) => {
  return (
    <Card className="mb-4 border">
      <Card.Header className="bg-white">
        <div className="d-flex align-items-center justify-content-between">
          <h6 className="mb-0">
            Vehicles <span className="text-danger">*</span>
          </h6>
        </div>
      </Card.Header>
      <Card.Body>
        {fieldErrors.vehicles && (
          <Alert variant="danger" className="mb-3">
            {fieldErrors.vehicles}
          </Alert>
        )}

        {form.vehicles.map((vehicle, index) => (
          <BookingVehicleRow
            key={index}
            vehicle={vehicle}
            index={index}
            vehicles={vehicles}
            vehicleRowsCount={form.vehicles.length}
            selectedVehicleIds={selectedVehicleIds}
            fieldErrors={fieldErrors}
            isCreatingBooking={isCreatingBooking}
            onVehicleChange={onVehicleChange}
            onRemoveVehicle={onRemoveVehicle}
          />
        ))}

        <Button
          type="button"
          size="sm"
          variant="outline-primary"
          onClick={onAddVehicle}
          disabled={isCreatingBooking}
        >
          <i className="feather icon-plus"></i> Add Vehicle
        </Button>
      </Card.Body>
    </Card>
  );
};

export default BookingVehiclesSection;
